import Button from '@/common/components/Button'
import SelectController from '@/common/components/SelectController'
import Skeleton from '@/common/components/Skeleton'
import { Mapper } from '@/common/services/mapper'
import {
  contactStateStrategy,
  translateContactStateStrategy,
} from '@/features/publicCompanyLeadsFunnel/constants/stateStrategy.constant'
import { useCreatePublicCompanyLeadContact } from '@/features/publicCompanyLeadsFunnel/hooks/useCreatePublicCompanyLeadContact'
import { PublicCompanyLeadsFunnelUpdateValidationSchema } from '@/features/publicCompanyLeadsFunnel/schemas/update.schema'
import {
  ContactData,
  ContactState,
  PublicCompanyLeadData,
} from '@/features/publicCompanyLeadsFunnel/types/publicCompanyLead'
import { useIsFetching } from '@tanstack/react-query'
import { UserIcon } from 'lucide-react'
import { useFormContext } from 'react-hook-form'
import { Link } from 'react-router-dom'
import { twMerge } from 'tailwind-merge'

interface ContactsSectionProps {
  publicCompanyLeadId: string
  leadState: PublicCompanyLeadData['state']
  contacts?: ContactData[]
  contact_for_master_user?: ContactData
  submit?: () => void
  updateLeadOnSuccess?: () => void
  disabled?: boolean
}

export const ContactsSection = ({
  publicCompanyLeadId,
  leadState,
  contacts,
  contact_for_master_user,
  submit,
  updateLeadOnSuccess,
  disabled,
}: ContactsSectionProps) => {
  const { control } = useFormContext<PublicCompanyLeadsFunnelUpdateValidationSchema>()

  const isLeadFetching = useIsFetching({ queryKey: ['publicCompanyLead'] })

  const {
    mutateAsync: createPublicCompanyLeadContact,
    isPending: createPublicCompanyLeadContactIsPending,
    isError: createPublicCompanyLeadContactIsError,
  } = useCreatePublicCompanyLeadContact({
    publicCompanyLeadId,
    onSuccess: updateLeadOnSuccess,
  })

  return (
    <div className="flex flex-col items-start gap-y-4 pb-1">
      {createPublicCompanyLeadContactIsError && (
        <p className="text-sm text-danger-soft">Ocorreu um erro no servidor, cheque os campos e tente novamente</p>
      )}
      <p className="text-[18px] font-bold">Contatos</p>
      {leadState === 'contracting' && (
        <Button
          type="button"
          variant="link"
          tooltipPlace='right'
          operationKlassToCheckPermission='Admin::Funnel::Public::Leads::Create'
          onClick={() => createPublicCompanyLeadContact()}
          disabled={createPublicCompanyLeadContactIsPending}
          name="add_contact"
        >
          Novo contato
        </Button>
      )}
      <div className="flex gap-x-4 overflow-x-auto">
        {!isLeadFetching &&
          contacts?.map((contact) => {
            const hasPassedEveryRequirement = contact.requirement_list_summary?.steps?.every(
              (step) => step.passed === true,
            )

            return (
              <Link
                to={`/public-clients/${publicCompanyLeadId}/contacts/${contact.id}/edit`}
                className={twMerge(
                  'flex min-w-fit gap-2 items-center bg-white-100 px-4 py-2 rounded-lg cursor-pointer hover:bg-white-200',
                  contact.state === ContactState.awaiting_client_documents && 'bg-yellow-op-2 hover:bg-yellow-hover',
                  contact.state === ContactState.in_review && 'bg-blue-700 hover:bg-primary-blue-hover',
                  ((hasPassedEveryRequirement && contact.state === ContactState.in_review) ||
                    !(['contracting'] as PublicCompanyLeadData['state'][]).includes(leadState)) &&
                  'bg-success-disabled hover:bg-success-hover',
                )}
                key={contact.id}
                data-testid="contact_link_button"
              >
                <div className="flex justify-center items-center size-8 bg-white-800 text-black-600 rounded-full">
                  <UserIcon />
                </div>
                <div>
                  <p>
                    <span className="font-bold text-sm">{contact.full_name}</span> -{' '}
                    <span className="italic">{contact.role_in_company}</span>
                  </p>
                  <p className="text-xs">
                    {(['contracting'] as PublicCompanyLeadData['state'][]).includes(leadState)
                      ? translateContactStateStrategy[
                      contactStateStrategy[contact.state as keyof typeof contactStateStrategy]
                      ]
                      : 'Visualizar'}
                  </p>
                </div>
              </Link>
            )
          })}

        {!!isLeadFetching && (
          <>
            <Skeleton height={55} width={160} className="bg-white-100" />
            <Skeleton height={55} width={160} className="bg-white-100" />
          </>
        )}
      </div>
      <SelectController
        control={control}
        name="contact_for_master_user"
        label="Gestor Master Frota"
        className="w-full"
        defaultValue={(contacts?.length
          ? contacts.map((contact) => ({ value: contact.id, label: contact.full_name || '' }))
          : []
        )?.find((contact) => contact.value === contact_for_master_user?.id)}
        options={
          contacts?.length
            ? Mapper.mapToOptions<ContactData>({ labelFieldName: 'full_name', valueFieldName: 'id' })(contacts)
            : []
        }
        triggerOnChange={submit}
        isDisabled={disabled || leadState !== 'contracting'}
        markAsRequired
      />
    </div>
  )
}
